import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, CardBody, CardHeader, Row, Col, Button, Badge, Table,
  Spinner, Input, Label,
} from "reactstrap";
import Select from "react-select";
import AccountingReportService from "../../../services/AccountingServices/AccountingReportService";
import LedgersService from "../../../services/AccountingServices/LedgerService";
import FiscalYearService from "../../../services/AccountingServices/FiscalYearService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import toast from "react-hot-toast";

const LedgerStatement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ledgers, setLedgers] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [statementData, setStatementData] = useState(null);

  const [filters, setFilters] = useState({
    ledgerId: null,
    from: "",
    to: "",
    fiscalYearCode: null,
  });

  useEffect(() => {
    fetchLedgers();
    fetchFiscalYears();
  }, []);

  const fetchLedgers = async () => {
    try {
      const response = await LedgersService.getList();
      const items = Array.isArray(response) ? response : response?.items || [];
      setLedgers(items.map((l) => ({ value: l.id, label: `${l.code} - ${l.name}` })));
    } catch (error) {
      console.error("Error fetching ledgers:", error);
    }
  };

  const fetchFiscalYears = async () => {
    try {
      const response = await FiscalYearService.getList();
      const items = Array.isArray(response) ? response : response?.items || [];
      setFiscalYears(items.map((fy) => ({ value: fy.code, label: fy.name })));
    } catch (error) {
      console.error("Error fetching fiscal years:", error);
    }
  };

  const handleGenerate = async () => {
    if (!filters.ledgerId) {
      toast.error("Please select a ledger");
      return;
    }
    setLoading(true);
    try {
      const params = {
        ledgerId: filters.ledgerId,
        from: filters.from || undefined,
        to: filters.to || undefined,
        fiscalYearCode: filters.fiscalYearCode || undefined,
      };
      const data = await AccountingReportService.getLedgerStatement(params);
      setStatementData(data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch ledger statement");
      setStatementData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) =>
    (val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "8px",
      borderColor: "#dee2e6",
      fontSize: "0.85rem",
      "&:hover": { borderColor: "#86b7fe" },
    }),
    option: (base) => ({ ...base, fontSize: "0.85rem" }),
  };

  const summaryCards = statementData
    ? [
        { title: "Opening Balance", value: statementData.openingBalance, icon: "ri-wallet-3-line", color: "#6366f1", bg: "rgba(99,102,241,0.08)" },
        { title: "Total Debit", value: statementData.totalDr, icon: "ri-arrow-up-circle-line", color: "#0ab39c", bg: "rgba(10,179,156,0.08)" },
        { title: "Total Credit", value: statementData.totalCr, icon: "ri-arrow-down-circle-line", color: "#f06548", bg: "rgba(240,101,72,0.08)" },
        { title: "Closing Balance", value: statementData.closingBalance, icon: "ri-funds-box-line", color: "#405189", bg: "rgba(64,81,137,0.08)" },
      ]
    : [];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Ledger Statement" pageTitle="Accounting" />
        {/* Page Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  boxShadow: "0 4px 14px rgba(99,102,241,0.3)",
                }}
              >
                <i className="ri-file-list-3-line fs-22 text-white"></i>
              </div>
              <div>
                <h4 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>Ledger Statement</h4>
                <p className="text-muted mb-0 small">View detailed transaction history for any ledger account</p>
              </div>
            </div>
          </Col>
          {/* <Col xs="auto">
            <Button
              color="soft-secondary"
              size="sm"
              onClick={() => navigate(-1)}
              className="d-flex align-items-center gap-1"
            >
              <i className="ri-arrow-left-line"></i> Back
            </Button>
          </Col> */}
        </Row>

        {/* Filters Card */}
        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: 12, overflow: "hidden" }}>
          <CardHeader
            className="border-0 py-3"
            style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="ri-filter-3-line text-primary fs-16"></i>
              <span className="fw-semibold text-dark">Report Filters</span>
            </div>
          </CardHeader>
          <CardBody className="p-3">
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Label className="form-label small fw-semibold text-muted mb-1">Ledger Account *</Label>
                <Select
                  options={ledgers}
                  value={ledgers.find((l) => l.value === filters.ledgerId)}
                  onChange={(opt) => setFilters((f) => ({ ...f, ledgerId: opt?.value || null }))}
                  placeholder="Select Ledger..."
                  isClearable
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </Col>
              <Col md={2}>
                <Label className="form-label small fw-semibold text-muted mb-1">Fiscal Year</Label>
                <Select
                  options={fiscalYears}
                  value={fiscalYears.find((fy) => fy.value === filters.fiscalYearCode)}
                  onChange={(opt) => setFilters((f) => ({ ...f, fiscalYearCode: opt?.value || null }))}
                  placeholder="Select FY..."
                  isClearable
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </Col>
              <Col md={2}>
                <Label className="form-label small fw-semibold text-muted mb-1">From Date</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                  style={{ borderRadius: 8, height: 38, fontSize: "0.85rem" }}
                />
              </Col>
              <Col md={2}>
                <Label className="form-label small fw-semibold text-muted mb-1">To Date</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                  style={{ borderRadius: 8, height: 38, fontSize: "0.85rem" }}
                />
              </Col>
              <Col md={3}>
                <Button
                  color="primary"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{
                    height: 38, borderRadius: 8, fontWeight: 600,
                    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    border: "none",
                  }}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="ri-file-search-line"></i> Generate Report
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        {statementData && (
          <>
            <Row className="mb-4 g-3">
              {summaryCards.map((card, idx) => (
                <Col md={3} sm={6} key={idx}>
                  <Card
                    className="shadow-sm border-0 h-100"
                    style={{
                      borderRadius: 12,
                      background: card.bg,
                      borderLeft: `4px solid ${card.color}`,
                      transition: "transform 0.2s, box-shadow 0.2s",
                      cursor: "default",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <CardBody className="p-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <p className="text-muted small fw-semibold mb-1 text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                            {card.title}
                          </p>
                          <h5 className="mb-0 fw-bold" style={{ color: card.color }}>
                            {formatCurrency(card.value)}
                          </h5>
                        </div>
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: 42, height: 42, borderRadius: 10,
                            background: `${card.color}15`,
                          }}
                        >
                          <i className={`${card.icon} fs-20`} style={{ color: card.color }}></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Ledger Info Bar */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <CardBody className="py-3 px-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: "rgba(99,102,241,0.1)",
                      }}
                    >
                      <i className="ri-book-2-line fs-18" style={{ color: "#6366f1" }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold text-dark">{statementData.ledgerName}</h6>
                      <span className="text-muted small">Code: {statementData.ledgerCode}</span>
                    </div>
                  </div>
                  <Badge
                    color="soft-primary"
                    className="px-3 py-2 fs-12"
                    style={{ borderRadius: 8 }}
                  >
                    {statementData.lines?.length || 0} Transactions
                  </Badge>
                </div>
              </CardBody>
            </Card>

            {/* Transaction Table */}
            <Card className="shadow-sm border-0" style={{ borderRadius: 12, overflow: "hidden" }}>
              <CardHeader
                className="border-0 py-3"
                style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-list-check-2 text-primary fs-16"></i>
                  <span className="fw-semibold text-dark">Transaction Details</span>
                </div>
              </CardHeader>
              <div className="table-responsive">
                <Table className="table-hover align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th className="text-muted fw-semibold small text-uppercase" style={{ width: 50, letterSpacing: "0.5px" }}>#</th>
                      <th className="text-muted fw-semibold small text-uppercase" style={{ letterSpacing: "0.5px" }}>Date</th>
                      <th className="text-muted fw-semibold small text-uppercase" style={{ letterSpacing: "0.5px" }}>Voucher SN</th>
                      <th className="text-muted fw-semibold small text-uppercase text-center" style={{ letterSpacing: "0.5px" }}>Type</th>
                      <th className="text-muted fw-semibold small text-uppercase" style={{ letterSpacing: "0.5px" }}>Description</th>
                      <th className="text-muted fw-semibold small text-uppercase text-end" style={{ letterSpacing: "0.5px" }}>Debit</th>
                      <th className="text-muted fw-semibold small text-uppercase text-end" style={{ letterSpacing: "0.5px" }}>Credit</th>
                      <th className="text-muted fw-semibold small text-uppercase text-end" style={{ letterSpacing: "0.5px" }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statementData.lines && statementData.lines.length > 0 ? (
                      statementData.lines.map((line, idx) => (
                        <tr
                          key={idx}
                          style={{
                            cursor: "pointer",
                            transition: "background 0.15s",
                          }}
                          onClick={() => line.voucherId && navigate(`/vouchers/${line.voucherId}`)}
                        >
                          <td className="text-muted">{idx + 1}</td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <i className="ri-calendar-line text-muted" style={{ fontSize: 12 }}></i>
                              <span>{formatDate(line.valueDate)}</span>
                            </div>
                          </td>
                          <td>
                            <span className="fw-semibold text-primary" style={{ fontSize: "0.82rem" }}>
                              {line.voucherSn}
                            </span>
                          </td>
                          <td className="text-center">
                            <Badge
                              color={
                                line.voucherType === "JOURNAL" ? "primary" :
                                line.voucherType === "INCOME" ? "success" :
                                line.voucherType === "PAYMENT" ? "warning" : "info"
                              }
                              className="text-uppercase px-2 py-1 fs-10"
                              style={{ minWidth: 65, borderRadius: 6 }}
                            >
                              {line.voucherType}
                            </Badge>
                          </td>
                          <td>
                            <span className="text-dark">{line.description}</span>
                            {line.remarks && (
                              <div className="text-muted small" style={{ fontSize: "0.75rem" }}>
                                {line.remarks}
                              </div>
                            )}
                          </td>
                          <td className="text-end">
                            {line.drAmount > 0 ? (
                              <span className="fw-semibold" style={{ color: "#0ab39c" }}>
                                {formatCurrency(line.drAmount)}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-end">
                            {line.crAmount > 0 ? (
                              <span className="fw-semibold" style={{ color: "#f06548" }}>
                                {formatCurrency(line.crAmount)}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-end">
                            <span className="fw-bold" style={{ color: "#405189" }}>
                              {formatCurrency(line.runningBalance)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center py-5">
                          <div className="text-muted">
                            <i className="ri-file-list-3-line fs-36 d-block mb-2 opacity-25"></i>
                            <p className="mb-0">No transactions found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {statementData.lines && statementData.lines.length > 0 && (
                    <tfoot>
                      <tr style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%)", fontWeight: 700 }}>
                        <td colSpan={5} className="text-end fw-bold text-dark pe-3">
                          TOTALS
                        </td>
                        <td className="text-end fw-bold" style={{ color: "#0ab39c" }}>
                          {formatCurrency(statementData.totalDr)}
                        </td>
                        <td className="text-end fw-bold" style={{ color: "#f06548" }}>
                          {formatCurrency(statementData.totalCr)}
                        </td>
                        <td className="text-end fw-bold" style={{ color: "#405189" }}>
                          {formatCurrency(statementData.closingBalance)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </div>
            </Card>
          </>
        )}

        {/* Empty State */}
        {!statementData && !loading && (
          <Card className="shadow-sm border-0 text-center py-5" style={{ borderRadius: 12 }}>
            <CardBody>
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 80, height: 80, borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)",
                }}
              >
                <i className="ri-file-search-line fs-36" style={{ color: "#6366f1" }}></i>
              </div>
              <h5 className="fw-bold text-dark mb-2">Generate a Ledger Statement</h5>
              <p className="text-muted mb-0" style={{ maxWidth: 400, margin: "0 auto" }}>
                Select a ledger account and optional date range above, then click <strong>Generate Report</strong> to view the transaction history.
              </p>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LedgerStatement;
